import { useMemo } from 'react';
import styled from 'styled-components';
import { api, BracketStage, Match } from '../api/client';
import { useAsync, Async } from '../components/Async';
import { Flag } from '../components/TeamBadge';
import { H2, Muted } from '../components/ui';
import { teamName } from '../lib/flags';
import { useStore } from '../stores/store';
import { buildGraph, createResolver, isLocked, pickWinner, decidedCount, Slot } from '../lib/simulate';

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 14px;
  font-size: 0.85rem;
  .grow { flex: 1; }
  button {
    background: ${({ theme }) => theme.colors.surfaceAlt};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 0.8rem;
    min-height: auto;
  }
`;

const Champion = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.gold}, #b9820a);
  color: #1a1300;
  border-radius: ${({ theme }) => theme.radius};
  padding: 14px;
  margin-bottom: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
`;

const MatchBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  margin-bottom: 10px;
`;

const SideRow = styled.button<{ $win: boolean; $fav: boolean; $pickable: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  text-align: left;
  border: none;
  border-left: 4px solid ${({ theme, $fav }) => ($fav ? theme.colors.gold : 'transparent')};
  background: ${({ theme, $win }) => ($win ? theme.colors.accent : 'transparent')};
  color: ${({ theme, $win }) => ($win ? theme.colors.accentText : theme.colors.text)};
  cursor: ${({ $pickable }) => ($pickable ? 'pointer' : 'default')};
  &:first-child { border-bottom: 1px solid ${({ theme }) => theme.colors.border}; }

  .nm { flex: 1; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .muted { color: ${({ theme }) => theme.colors.textMuted}; font-style: italic; font-weight: 500; }
  .check { font-weight: 800; }
`;

function Side({
  slot,
  isWinner,
  favorite,
  pickable,
  onPick,
}: {
  slot: Slot;
  isWinner: boolean;
  favorite: string | null;
  pickable: boolean;
  onPick: () => void;
}) {
  const known = slot.team !== null;
  return (
    <SideRow
      $win={isWinner}
      $fav={known && slot.team === favorite}
      $pickable={pickable && known}
      onClick={() => pickable && known && onPick()}
    >
      <Flag team={slot.team} size={20} />
      <span className={known ? 'nm' : 'nm muted'}>
        {known ? teamName(slot.team as string) : slot.label}
        {slot.projected && known ? ' ●' : ''}
      </span>
      {isWinner && <span className="check">✓</span>}
    </SideRow>
  );
}

function BracketSim({ stages }: { stages: BracketStage[] }) {
  const favorite = useStore((s) => s.favorite);
  const picks = useStore((s) => s.bracketPicks);
  const setBracketPicks = useStore((s) => s.setBracketPicks);
  const resetBracket = useStore((s) => s.resetBracket);

  const g = useMemo(() => buildGraph(stages), [stages]);
  const r = createResolver(g, picks);

  const champion = r.winner(104);
  const decided = decidedCount(g, picks);

  const handlePick = (m: Match, team: string) => {
    if (isLocked(m)) return;
    setBracketPicks(pickWinner(m.num as number, team, g, picks));
  };

  return (
    <>
      <Bar>
        <span className="grow">
          🎮 <strong>Simulá la llave</strong> · {decided}/{g.matches.length} definidos
        </span>
        <button onClick={resetBracket}>Reiniciar</button>
      </Bar>

      {champion && (
        <Champion>
          🏆 <Flag team={champion} size={30} /> Campeón: {teamName(champion)}
        </Champion>
      )}

      <Muted style={{ margin: '0 0 14px', fontSize: '0.78rem' }}>
        Tocá el equipo que pasa en cada partido. Al cambiar un resultado se borran las rondas que
        dependían de él. Tu simulación queda guardada en este dispositivo. ● = puesto proyectado.
      </Muted>

      {stages.map((s) => (
        <section key={s.key}>
          <H2>
            {s.key === 'final' ? '🏆 ' : ''}
            {s.label}
          </H2>
          {s.matches.map((m) => {
            const [s1, s2] = r.parts(m);
            const win = r.winner(m.num as number);
            const locked = isLocked(m);
            return (
              <MatchBox key={m.id}>
                <Side
                  slot={s1}
                  isWinner={win !== null && win === s1.team}
                  favorite={favorite}
                  pickable={!locked}
                  onPick={() => s1.team && handlePick(m, s1.team)}
                />
                <Side
                  slot={s2}
                  isWinner={win !== null && win === s2.team}
                  favorite={favorite}
                  pickable={!locked}
                  onPick={() => s2.team && handlePick(m, s2.team)}
                />
              </MatchBox>
            );
          })}
        </section>
      ))}
    </>
  );
}

export function Bracket() {
  const state = useAsync(() => api.bracket(), []);
  return <Async state={state}>{(d) => <BracketSim stages={d.stages} />}</Async>;
}
